import { Link } from "react-router-dom";
import {
  CheckCircle2, ArrowRight, AlertTriangle, Sparkles, ShieldCheck,
  Camera, Clock, Star, Phone, MessageCircle, Bath, ChefHat, Bed, Sofa, Home as HomeIcon,
} from "lucide-react";
import MarketingLayout from "../../components/MarketingLayout";
import { Brand, brandShort } from "../../context/CompanyContext";

// Pre-filled WhatsApp link reused by the 2 CTA stacks below. Message is
// slightly more context-aware than the global FAB since the visitor is
// already reading the detailed cleaning page.
const WHATSAPP_URL =
  "https://wa.me/351967386076?text=" +
  encodeURIComponent(
    `Olá ${brandShort()}! Vi a página de limpezas e quero um orçamento para o meu apartamento.`,
  );

/**
 * "Limpeza incluída" — high-conversion sales page that walks a host
 * through the *experience* of LuxStay rather than dumping a flat
 * checklist on them. Layout is a classic problem→solution→proof→offer
 * funnel adapted to the dark-luxe brand:
 *   1. Emotional hero (one-line promise)
 *   2. Pain points (the cost of mediocre cleaning)
 *   3. The LuxStay difference (3 pillars)
 *   4. Detailed checklist by room
 *   5. Photo report explainer
 *   6. Social proof (1 testimonial)
 *   7. Risk-reversal guarantee
 *   8. Final CTA stack (form link + phone)
 */
export default function LimpezaIncluida() {
  return (
    <MarketingLayout>
      {/* ───────────────────────  HERO  ─────────────────────── */}
      <section className="bg-[#041f1b] py-14 sm:py-20" data-testid="li-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#d4af37]">
            <Sparkles size={12} /> Padrão hoteleiro · sem desculpas
          </span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#fdfbf7] leading-[1.05]"
              data-testid="li-headline">
            Quando o hóspede entra, devia <span className="italic text-[#d4af37]">parar</span>{" "}
            por um instante.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[#a0b2af] max-w-2xl mx-auto leading-relaxed"
             data-testid="li-subheadline">
            Cheiro a fresco. Cama esticada. Bancada a brilhar. É essa a primeira impressão
            que decide as suas avaliações de 5 estrelas — e nós tratamos disso por si.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
            <Link to="/#contacto"
                  className="inline-flex justify-center items-center gap-2 px-7 py-4 text-base sm:text-lg font-semibold rounded-sm uppercase tracking-widest text-[#031411] bg-[#d4af37] hover:bg-[#f5d76e] shadow-lg shadow-[#d4af37]/25 transition-colors focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
                  data-testid="li-cta-primary">
              Quero este nível de limpeza <ArrowRight size={18} />
            </Link>
            <a href={WHATSAPP_URL}
               target="_blank" rel="noopener noreferrer"
               className="inline-flex justify-center items-center gap-2 px-7 py-4 text-base font-semibold rounded-sm uppercase tracking-widest text-white bg-[#25D366] hover:bg-[#1ebe5a] shadow-lg shadow-[#25D366]/25 transition-colors focus:outline-none focus:ring-2 focus:ring-[#25D366]/60"
               data-testid="li-cta-whatsapp">
              <MessageCircle size={18} fill="currentColor" /> WhatsApp
            </a>
            <a href="tel:+351936942709"
               className="inline-flex justify-center items-center gap-2 px-7 py-4 text-base font-semibold rounded-sm uppercase tracking-widest text-[#d4af37] border-2 border-[#d4af37]/50 hover:bg-[#d4af37]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
               data-testid="li-cta-phone">
              <Phone size={18} /> Ligar
            </a>
          </div>
        </div>
      </section>

      {/* ───────────────────  PAIN POINTS  ───────────────────── */}
      <section className="bg-[#031411] border-y border-[#1a4640] py-14" data-testid="li-pain">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]">
              O verdadeiro custo
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-[#fdfbf7] max-w-2xl mx-auto leading-tight">
              Uma limpeza fraca vale-lhe <span className="italic text-[#d4af37]">muito mais</span> que o serviço barato.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {[
              { title: "Avaliações 3★ ou 4★",
                body: "Uma única foto de pó na mesa-de-cabeceira derruba a sua nota global. Recuperar custa meses." },
              { title: "Reservas perdidas",
                body: "Hóspedes leem reviews antes de reservar. Cada estrela em baixo pode tirar-lhe 30% das reservas." },
              { title: "Tempo a controlar tudo",
                body: "Mensagens, check-ins, fotos para confirmar... Acaba a fazer o trabalho da equipa de limpeza." },
            ].map(p => (
              <article key={p.title}
                       className="bg-[#062c27] p-6 rounded-2xl border border-[#1a4640]"
                       data-testid={`li-pain-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-10 h-10 rounded-xl bg-[#dc2626]/15 grid place-items-center text-[#fca5a5] mb-4">
                  <AlertTriangle size={20} />
                </div>
                <h3 className="font-display text-lg text-[#fdfbf7]">{p.title}</h3>
                <p className="mt-1.5 text-sm text-[#a0b2af] leading-relaxed">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────  THE LUXSTAY DIFFERENCE  ───────────────── */}
      <section className="py-14 sm:py-20" data-testid="li-pillars">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]">
              Como fazemos diferente
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-[#fdfbf7] max-w-2xl mx-auto leading-tight">
              Três regras que mudam <span className="italic text-[#d4af37]">tudo</span>.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {[
              { Icon: ShieldCheck, title: "Equipa fixa para si",
                body: "As mesmas pessoas vão ao seu apartamento, sempre. Conhecem cada gaveta — não há perguntas." },
              { Icon: Camera, title: "Relatório fotográfico",
                body: "Antes/depois enviado a si após cada limpeza. Confirma à distância, sem dúvidas." },
              { Icon: Sparkles, title: "Verificação dupla",
                body: "Cada item da checklist é validado pelo responsável antes de finalizar. Zero atalhos." },
            ].map(({ Icon, title, body }) => (
              <article key={title}
                       className="relative bg-[#062c27] p-7 rounded-2xl border border-[#1a4640] hover:border-[#d4af37]/60 hover:shadow-lg hover:shadow-[#d4af37]/10 hover:-translate-y-0.5 transition-all duration-200"
                       data-testid={`li-pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-12 h-12 rounded-xl bg-[#d4af37]/15 grid place-items-center text-[#d4af37]">
                  <Icon size={22} />
                </div>
                <h3 className="mt-4 font-display text-xl text-[#fdfbf7]">{title}</h3>
                <p className="mt-2 text-sm text-[#a0b2af] leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────  THE CHECKLIST  ──────────────────── */}
      <section className="bg-[#031411] border-y border-[#1a4640] py-14 sm:py-20" data-testid="li-checklist">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]">
              Lista detalhada
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-[#fdfbf7] max-w-2xl mx-auto leading-tight">
              O que fica feito em <span className="italic text-[#d4af37]">cada limpeza</span>.
            </h2>
            <p className="mt-4 text-[#a0b2af] max-w-2xl mx-auto">
              Sem letras pequenas. Estes são os pontos que pode confirmar no seu relatório fotográfico.
            </p>
          </div>

          <div className="mt-12 space-y-5">
            {GROUPS.map(({ Icon, title, items }) => (
              <article key={title}
                       className="bg-[#062c27] rounded-2xl border border-[#1a4640] p-6 sm:p-8 hover:border-[#d4af37]/60 transition-colors"
                       data-testid={`li-group-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#d4af37]/15 grid place-items-center text-[#d4af37]">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-2xl text-[#fdfbf7]">{title}</h3>
                </div>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {items.map(it => (
                    <li key={it} className="flex items-start gap-2 text-sm text-[#fdfbf7]">
                      <CheckCircle2 size={16} className="text-[#d4af37] shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────  TESTIMONIAL + GUARANTEE  ─────────── */}
      <section className="py-14 sm:py-20" data-testid="li-proof">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          {/* Testimonial */}
          <article className="bg-[#062c27] p-7 sm:p-10 rounded-2xl border border-[#1a4640] flex flex-col"
                   data-testid="li-testimonial">
            <div className="flex items-center gap-1 text-[#d4af37]" aria-label="Avaliação 5 estrelas">
              {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" stroke="currentColor" />)}
            </div>
            <p className="mt-5 text-[#fdfbf7] text-lg leading-relaxed flex-1">
              «Antes da <Brand short />, eu sentia-me a controlar pessoas em vez de receber hóspedes.
              Agora cada check-out é tratado, vejo as fotos no telemóvel e acabou. As avaliações
              de 5★ falam por si.»
            </p>
            <div className="mt-6 pt-6 border-t border-[#1a4640] flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#d4af37]/15 text-[#d4af37] grid place-items-center font-bold">M</div>
              <div>
                <div className="font-semibold text-[#fdfbf7] text-sm">Marta C.</div>
                <div className="text-xs text-[#a0b2af]">Anfitriã · Funchal · 4 apartamentos</div>
              </div>
            </div>
          </article>

          {/* Guarantee */}
          <article className="relative overflow-hidden bg-gradient-to-br from-[#062c27] via-[#031411] to-[#062c27] p-7 sm:p-10 rounded-2xl border border-[#d4af37]/40"
                   data-testid="li-guarantee">
            <div aria-hidden="true"
                 className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#d4af37]/25 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-[#d4af37] grid place-items-center text-[#031411]">
                <ShieldCheck size={22} />
              </div>
              <h3 className="mt-5 font-display text-2xl sm:text-3xl tracking-tight text-[#fdfbf7]">
                Satisfação <span className="italic text-[#d4af37]">garantida</span>
              </h3>
              <p className="mt-3 text-[#a0b2af] leading-relaxed">
                Se houver alguma coisa que não cumpra os nossos padrões, voltamos ao seu
                apartamento <strong className="text-[#fdfbf7]">sem custo nenhum</strong> nas
                24h seguintes para corrigir. Simples assim.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm">
                {[
                  "Sem fidelização — saia quando quiser",
                  "Resposta a pedidos em menos de 24h",
                  "Equipa local da Madeira, fardada",
                ].map(b => (
                  <li key={b} className="flex items-center gap-2.5 text-[#fdfbf7]">
                    <CheckCircle2 size={16} className="text-[#d4af37] shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </section>

      {/* ─────────────────────  FINAL CTA  ──────────────────── */}
      <section className="bg-[#031411] border-t border-[#1a4640] py-14 sm:py-20" data-testid="li-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Clock size={32} className="text-[#d4af37] mx-auto" />
          <h2 className="mt-5 font-display text-3xl sm:text-4xl tracking-tight text-[#fdfbf7] leading-tight">
            Próximo check-out: já está <span className="italic text-[#d4af37]">tratado</span>?
          </h2>
          <p className="mt-4 text-[#a0b2af]">
            Peça o seu orçamento gratuito agora — sem compromisso, resposta em menos de 24h.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
            <Link to="/#contacto"
                  className="inline-flex justify-center items-center gap-2 px-7 py-4 text-base font-semibold rounded-sm uppercase tracking-widest text-[#031411] bg-[#d4af37] hover:bg-[#f5d76e] shadow-lg shadow-[#d4af37]/25 transition-colors focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
                  data-testid="li-final-cta-primary">
              Pedir orçamento gratuito <ArrowRight size={18} />
            </Link>
            <a href={WHATSAPP_URL}
               target="_blank" rel="noopener noreferrer"
               className="inline-flex justify-center items-center gap-2 px-7 py-4 text-base font-semibold rounded-sm uppercase tracking-widest text-white bg-[#25D366] hover:bg-[#1ebe5a] shadow-lg shadow-[#25D366]/25 transition-colors focus:outline-none focus:ring-2 focus:ring-[#25D366]/60"
               data-testid="li-final-cta-whatsapp">
              <MessageCircle size={18} fill="currentColor" /> WhatsApp
            </a>
            <a href="tel:+351936942709"
               className="inline-flex justify-center items-center gap-2 px-7 py-4 text-base font-semibold rounded-sm uppercase tracking-widest text-[#d4af37] border-2 border-[#d4af37]/50 hover:bg-[#d4af37]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#d4af37]"
               data-testid="li-final-cta-phone">
              <Phone size={18} /> +351 936 942 709
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

const GROUPS = [
  { Icon: Bed, title: "Quartos",
    items: ["Mudança completa de roupa de cama", "Aspiração e/ou lavagem de chão",
            "Limpeza de superfícies, mesas-de-cabeceira e candeeiros",
            "Reposição da decoração no lugar"] },
  { Icon: Bath, title: "Casas de banho",
    items: ["Sanita, bidé e chuveiro a fundo (anti-calcário)", "Limpeza de espelhos e bancadas",
            "Reposição de toalhas e amenities", "Saco do lixo + verificação de papel/sabão"] },
  { Icon: ChefHat, title: "Cozinha",
    items: ["Loiça lavada e arrumada", "Bancadas, placa, fogão e exaustor",
            "Frigorífico (interior + exterior)", "Forno e micro-ondas",
            "Lava-loiça, torneiras e chão"] },
  { Icon: Sofa, title: "Sala / áreas comuns",
    items: ["Aspiração de sofás e tapetes", "Almofadas e mantas dobradas",
            "Pó em mobiliário, decoração e quadros", "Limpeza de televisor e comandos"] },
  { Icon: HomeIcon, title: "Detalhes finais",
    items: ["Janelas e estores", "Maçanetas, interruptores e telefones desinfetados",
            "Aroma neutro / fresco", "Foto antes/depois enviada ao cliente"] },
];
